import { useLayoutEffect, useState } from 'react';

import useMediaQuery from '@mui/material/useMediaQuery';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import NavItem from './NavItem';
import NavGroup from './NavGroup';
import menuItem, { getMenuItemsByRole } from 'menu-items';
import { applyCatalogueIcons, buildCatalogueIconIndex } from 'menu-items/dynamicMenu';

import useConfig from 'hooks/useConfig';
import useAuth from 'hooks/useAuth';
import useOnlineStatus from 'hooks/useOnlineStatus';
import useMyFeatures from 'hooks/useMyFeatures';
import useCatalogueVisibility from 'hooks/useCatalogueVisibility';
import useMyMenu from 'hooks/useMyMenu';
import { HORIZONTAL_MAX_ITEM, MenuOrientation } from 'config';
import { useGetMenu, useGetMenuMaster } from 'api/menu';

// The `isFound` helper, the `dashboardMenu` value and the MenuFromAPI import were
// removed with the commented-out dashboard-menu block below: they were referenced
// only from inside those comments, so eslint reported them as unused errors and
// this file could not pass the pre-commit hook. Restore them from git alongside
// that block if the API-driven dashboard menu comes back.

// ==============================|| DRAWER CONTENT - NAVIGATION ||============================== //

export default function Navigation() {
  const { menuOrientation } = useConfig();
  const { user } = useAuth();
  const { offlineUser } = useOnlineStatus();
  const { menuLoading } = useGetMenu();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));
  // Product-tier entitlement for this user's organization. `features` is null
  // until the lookup lands; the menu is unfiltered until then.
  const myFeatures = useMyFeatures();
  const entitledFeatures = myFeatures?.is_platform ? null : (myFeatures?.features ?? null);
  // Which catalogue features/pages are switched off in Features and Pages. A
  // SEPARATE gate from the tier entitlement above, and one that applies to platform
  // accounts too — `entitledFeatures` is deliberately null for them, so without
  // this an Ayro IT Admin saw every item regardless of the switch, which is why
  // toggling User Management off appeared to do nothing.
  const visibility = useCatalogueVisibility();
  // The navigation this user's roles actually grant. Takes precedence over the
  // static per-role trees — see getMenuItemsByRole.
  const myMenu = useMyMenu();

  const [selectedID, setSelectedID] = useState('');
  const [selectedItems, setSelectedItems] = useState('');
  const [selectedLevel, setSelectedLevel] = useState(0);
  const [menuItems, setMenuItems] = useState({ items: [] });

  // Use online user or fallback to offline cached user
  const activeUser = user || offlineUser;

  useLayoutEffect(() => {
    // Hidden: Dashboard menu (default, analytics, invoice)
    // Original code commented out to hide dashboard menu
    // if (menuLoading && !isFound(menuItem, 'group-dashboard-loading')) {
    //   menuItem.items.splice(0, 0, dashboardMenu);
    //   setMenuItems({ items: [...menuItem.items] });
    // } else if (!menuLoading && dashboardMenu?.id !== undefined && !isFound(menuItem, 'group-dashboard')) {
    //   menuItem.items.splice(0, 1, dashboardMenu);
    //   setMenuItems({ items: [...menuItem.items] });
    // } else {
    //   setMenuItems({ items: [...menuItem.items] });
    // }

    // Menu items for the role, narrowed by what the organization's product tier
    // actually entitles it to. `features` is null until the lookup returns, and
    // nothing is gated in that window — see getAdminMenuForLevel.
    const roleBasedMenu = activeUser?.role ? getMenuItemsByRole(activeUser.role, entitledFeatures, visibility, myMenu) : menuItem;
    // Make the icon set in Features and Components authoritative: override every
    // menu item's icon with the one the catalogue defines for its feature, so an
    // admin editing an icon there sees it change in the sidebar (and anywhere the
    // menu tree feeds) without a code change. Items the catalogue does not know
    // about keep their static icon. Falls open (no override) until my-menu lands.
    const withIcons = applyCatalogueIcons([...roleBasedMenu.items], buildCatalogueIconIndex(myMenu));
    setMenuItems({ items: withIcons });
  }, [menuLoading, activeUser, entitledFeatures, visibility, myMenu]);

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  const lastItem = isHorizontal ? HORIZONTAL_MAX_ITEM : null;
  let lastItemIndex = menuItems.items.length - 1;
  let remItems = [];
  let lastItemId;

  //  first it checks menu item is more than giving HORIZONTAL_MAX_ITEM after that get lastItemid by giving horizontal max
  // item and it sets horizontal menu by giving horizontal max item lastly slice menuItem from array and set into remItems

  if (lastItem && lastItem < menuItems.items.length) {
    lastItemId = menuItems.items[lastItem - 1].id;
    lastItemIndex = lastItem - 1;
    remItems = menuItems.items.slice(lastItem - 1, menuItems.items.length).map((item) => ({
      title: item.title,
      elements: item.children,
      icon: item.icon,
      ...(item.url && {
        url: item.url
      })
    }));
  }

  const navGroups = menuItems.items.slice(0, lastItemIndex + 1).map((item, index) => {
    switch (item.type) {
      case 'group':
        if (item.url && item.id !== lastItemId) {
          return (
            <List key={item.id} sx={{ zIndex: 0, ...(isHorizontal && { mt: 0.5 }) }}>
              {!isHorizontal && index !== 0 && <Divider sx={{ my: 0.5 }} />}
              <NavItem item={item} level={1} isParents setSelectedID={setSelectedID} />
            </List>
          );
        }

        return (
          <NavGroup
            key={item.id}
            setSelectedID={setSelectedID}
            setSelectedItems={setSelectedItems}
            setSelectedLevel={setSelectedLevel}
            selectedLevel={selectedLevel}
            selectedID={selectedID}
            selectedItems={selectedItems}
            lastItem={lastItem}
            remItems={remItems}
            lastItemId={lastItemId}
            item={item}
          />
        );
      default:
        return (
          <Typography key={item.id} variant="h6" color="error" align="center">
            Fix - Navigation Group
          </Typography>
        );
    }
  });

  return (
    <Box
      sx={{
        pt: drawerOpen ? (isHorizontal ? 0 : 2) : 0,
        ...(!isHorizontal && { '& > ul:first-of-type': { mt: 0 } }),
        display: isHorizontal ? { xs: 'block', lg: 'flex' } : 'block'
      }}
    >
      {navGroups}
    </Box>
  );
}
